import { useCallback, useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckIcon from "@mui/icons-material/Check";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockIcon from "@mui/icons-material/Lock";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";

import { extractPositionedText } from "./core/pdf/extract";
import { convertItems, UnknownBankError } from "./core/convert";
import { toCsv } from "./core/export/csv";
import { toXlsx } from "./core/export/xlsx";
import type { ParseResult } from "./core/types";
import { STRINGS, LANGS, initialLang, fmtMoney, type Lang, type Strings } from "./i18n";
import {
  CHECKOUT_URL,
  canConvert,
  isPro,
  recordConversion,
  setPro,
  trackEvent,
  validateLicense,
} from "./pro/pro";

interface StatusMsg {
  text: string;
  error: boolean;
}

export default function App() {
  const [lang, setLang] = useState<Lang>(initialLang);
  const [result, setResult] = useState<ParseResult | null>(null);
  const [status, setStatus] = useState<{ key: keyof Strings; error: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [pro, setProState] = useState<boolean>(isPro);
  const [proOpen, setProOpen] = useState(false);
  const baseName = useRef("statement");
  const fileInput = useRef<HTMLInputElement>(null);

  const t = STRINGS[lang];
  const fmt = useCallback((n: number) => fmtMoney(n, lang), [lang]);

  useEffect(() => {
    document.documentElement.lang = lang;
    try {
      localStorage.setItem("lang", lang);
    } catch {
      /* ignore */
    }
  }, [lang]);

  const handleFile = useCallback(async (file: File) => {
    setResult(null);
    setStatus(null);
    baseName.current = file.name.replace(/\.pdf$/i, "") || "statement";

    if (file.type && file.type !== "application/pdf") {
      setStatus({ key: "errNotPdf", error: true });
      return;
    }

    // Free-tier gate (dormant until the paywall is enabled with a live checkout).
    if (!canConvert()) {
      trackEvent("paywall-hit");
      setProOpen(true);
      return;
    }

    setBusy(true);
    setStatus({ key: "processing", error: false });
    try {
      const buf = await file.arrayBuffer();
      const items = await extractPositionedText(buf);
      if (items.length === 0) {
        setStatus({ key: "errNoText", error: true });
        return;
      }
      setResult(convertItems(items));
      recordConversion();
      setStatus(null);
    } catch (err) {
      if (err instanceof UnknownBankError) {
        setStatus({ key: "errUnknownBank", error: true });
      } else {
        console.error(err);
        setStatus({ key: "errGeneric", error: true });
      }
    } finally {
      setBusy(false);
    }
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const f = e.dataTransfer.files?.[0];
      if (f) void handleFile(f);
    },
    [handleFile],
  );

  const downloadCsv = () => {
    if (!result) return;
    download(
      new Blob([toCsv(result)], { type: "text/csv;charset=utf-8" }),
      `${baseName.current}.csv`,
    );
  };
  const downloadXlsx = () => {
    if (!result) return;
    const bytes = toXlsx(result);
    download(
      new Blob([bytes.buffer as ArrayBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `${baseName.current}.xlsx`,
    );
  };

  const statusText: StatusMsg | null = status
    ? { text: t[status.key] as string, error: status.error }
    : null;

  return (
    <Box sx={{ minHeight: "100dvh", display: "flex", flexDirection: "column" }}>
      <AppBar position="static" color="primary" elevation={0}>
        <Toolbar sx={{ gap: 1 }}>
          <DescriptionIcon sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            BalkanStatements
          </Typography>
          <Chip
            icon={<LockIcon sx={{ fontSize: 16 }} />}
            label={t.localBadge}
            size="small"
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              color: "inherit",
              bgcolor: "rgba(255,255,255,0.16)",
            }}
          />
          {pro ? (
            <Chip
              icon={<WorkspacePremiumIcon sx={{ fontSize: 16 }} />}
              label={t.pro.active}
              size="small"
              color="success"
            />
          ) : (
            <Button
              size="small"
              variant="outlined"
              startIcon={<WorkspacePremiumIcon />}
              onClick={() => {
                trackEvent("pro-open");
                setProOpen(true);
              }}
              sx={{ color: "inherit", borderColor: "rgba(255,255,255,0.6)" }}
            >
              {t.pro.btn}
            </Button>
          )}
          <LanguageSwitcher lang={lang} onChange={setLang} />
        </Toolbar>
      </AppBar>

      <ProUpgradeDialog
        open={proOpen}
        t={t}
        onClose={() => setProOpen(false)}
        onUnlocked={() => {
          setProState(true);
          setProOpen(false);
        }}
      />

      <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 }, flexGrow: 1 }}>
        {/* Hero */}
        <Stack spacing={1.5} sx={{ mb: 4, textAlign: "center" }}>
          <Typography variant="h4" component="h1">
            {t.heroTitle}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t.heroLead.a}
            <strong>{t.heroLead.mid}</strong>
            {t.heroLead.c}
          </Typography>
        </Stack>

        {/* Dropzone */}
        <input
          ref={fileInput}
          type="file"
          accept="application/pdf"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleFile(f);
            e.target.value = "";
          }}
        />
        <Paper
          variant="outlined"
          onClick={() => fileInput.current?.click()}
          onDragEnter={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          sx={{
            p: { xs: 4, md: 6 },
            textAlign: "center",
            cursor: "pointer",
            borderStyle: "dashed",
            borderWidth: 2,
            borderColor: dragOver ? "primary.main" : "divider",
            bgcolor: dragOver ? "action.hover" : "background.paper",
            transition: "border-color .15s, background-color .15s",
          }}
        >
          <UploadFileIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            {t.dropTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t.dropSub}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
            {t.dropNote}
          </Typography>
        </Paper>

        {busy && <LinearProgress sx={{ mt: 2, borderRadius: 1 }} />}

        {statusText && (
          <Alert severity={statusText.error ? "error" : "info"} sx={{ mt: 2 }}>
            {statusText.text}
          </Alert>
        )}

        {result && <ResultView result={result} t={t} fmt={fmt} onCsv={downloadCsv} onXlsx={downloadXlsx} />}

        {/* Why accurate */}
        <Paper variant="outlined" sx={{ p: 3, mt: 5 }}>
          <Typography variant="h6" gutterBottom>
            {t.whyTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t.whyBody.a}
            <em>{t.whyBody.mid}</em>
            {t.whyBody.c}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary">
            {t.sendSample.a}
            <Link href="mailto:hello@mamadra.dev?subject=Novi%20izvod">{t.sendSample.mid}</Link>
            {t.sendSample.c}
          </Typography>
        </Paper>
      </Container>

      <Box component="footer" sx={{ py: 2, textAlign: "center" }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          {t.footer}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          <Link href={`/${lang}/`} underline="hover">
            {lang === "sr" ? "Podržane banke" : lang === "ru" ? "Поддерживаемые банки" : "Supported banks"}
          </Link>
          {" · "}
          {LANGS.map((l, i) => (
            <span key={l.code}>
              {i > 0 && " · "}
              <Link href={`/${l.code}/`} underline="hover">
                {l.label}
              </Link>
            </span>
          ))}
        </Typography>
      </Box>
    </Box>
  );
}

function LanguageSwitcher({ lang, onChange }: { lang: Lang; onChange: (l: Lang) => void }) {
  return (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={lang}
      onChange={(_, v: Lang | null) => v && onChange(v)}
      aria-label="Language"
      sx={{
        "& .MuiToggleButton-root": {
          color: "rgba(255,255,255,0.75)",
          borderColor: "rgba(255,255,255,0.4)",
          px: 1.25,
          py: 0.25,
          lineHeight: 1.6,
          fontWeight: 600,
        },
        "& .MuiToggleButton-root.Mui-selected": {
          color: "primary.main",
          bgcolor: "common.white",
          "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
        },
      }}
    >
      {LANGS.map((l) => (
        <ToggleButton key={l.code} value={l.code} aria-label={l.label}>
          {l.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

function ProUpgradeDialog({
  open,
  t,
  onClose,
  onUnlocked,
}: {
  open: boolean;
  t: Strings;
  onClose: () => void;
  onUnlocked: () => void;
}) {
  const [key, setKey] = useState("");
  const [checking, setChecking] = useState(false);
  const [err, setErr] = useState(false);

  const onCta = () => {
    trackEvent("pro-cta");
    if (CHECKOUT_URL) window.open(CHECKOUT_URL, "_blank", "noopener");
    else
      window.location.href =
        "mailto:hello@mamadra.dev?subject=" + encodeURIComponent("BalkanStatements Pro");
  };

  const onUnlock = async () => {
    const k = key.trim();
    if (!k) return;
    setChecking(true);
    setErr(false);
    const ok = await validateLicense(k);
    setChecking(false);
    if (ok) {
      setPro(true);
      onUnlocked();
    } else {
      setErr(true);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <WorkspacePremiumIcon color="primary" /> {t.pro.title}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t.pro.lede}
        </Typography>
        <Stack component="ul" spacing={0.75} sx={{ listStyle: "none", pl: 0, my: 1.5 }}>
          {t.pro.benefits.map((b, i) => (
            <Stack key={i} component="li" direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
              <CheckIcon color="success" sx={{ fontSize: 18, mt: "2px", flexShrink: 0 }} />
              <Typography variant="body2">{b}</Typography>
            </Stack>
          ))}
        </Stack>
        <Button
          fullWidth
          variant="contained"
          startIcon={<WorkspacePremiumIcon />}
          onClick={onCta}
          sx={{ mt: 1 }}
        >
          {t.pro.cta}
        </Button>
        {!CHECKOUT_URL && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1, textAlign: "center" }}
          >
            {t.pro.soon}
          </Typography>
        )}
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" gutterBottom>
          {t.pro.haveKey}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
          <TextField
            size="small"
            fullWidth
            placeholder={t.pro.keyPlaceholder}
            value={key}
            onChange={(e) => {
              setKey(e.target.value);
              setErr(false);
            }}
            error={err}
            helperText={err ? t.pro.unlockError : ""}
          />
          <Button variant="outlined" onClick={onUnlock} disabled={checking || !key.trim()}>
            {t.pro.unlock}
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t.pro.close}</Button>
      </DialogActions>
    </Dialog>
  );
}

function ResultView({
  result,
  t,
  fmt,
  onCsv,
  onXlsx,
}: {
  result: ParseResult;
  t: Strings;
  fmt: (n: number) => string;
  onCsv: () => void;
  onXlsx: () => void;
}) {
  const { meta, reconciliation: rc, transactions, warnings } = result;
  const hasBalances = rc.openingBalance != null && rc.closingBalance != null;

  // Headline + detail are generated here (localized) rather than taken from the
  // core, so they follow the selected language.
  const headline = !hasBalances
    ? t.reconNoBalance
    : rc.ok
      ? t.reconOk
      : t.reconMismatch(fmt(rc.difference ?? 0));
  const detail = hasBalances
    ? t.reconDetail({
        opening: fmt(rc.openingBalance ?? 0),
        computed: fmt(rc.computedClosing ?? 0),
        closing: fmt(rc.closingBalance ?? 0),
        diff: fmt(rc.difference ?? 0),
        ok: rc.ok,
      })
    : t.reconNoBalance;

  return (
    <Box sx={{ mt: 3 }}>
      <Alert
        severity={rc.ok ? "success" : "warning"}
        iconMapping={{ success: <CheckCircleIcon fontSize="inherit" /> }}
        sx={{ mb: 2, alignItems: "center" }}
      >
        <Typography variant="subtitle2">{headline}</Typography>
        <Typography variant="caption" color="text.secondary">
          {detail}
        </Typography>
      </Alert>

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
        <Chip label={`${t.bank}: ${meta.bankName}`} />
        {meta.accountId && <Chip variant="outlined" label={`${t.account}: ${meta.accountId}`} />}
        {meta.periodStart && (
          <Chip
            variant="outlined"
            label={`${t.period}: ${meta.periodStart} — ${meta.periodEnd ?? ""}`}
          />
        )}
        <Chip variant="outlined" label={`${t.currency}: ${meta.currency}`} />
      </Stack>

      <Stack direction="row" spacing={1.5} sx={{ mb: 2, alignItems: "center", flexWrap: "wrap" }}>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={onCsv}>
          CSV
        </Button>
        <Button variant="contained" startIcon={<DownloadIcon />} onClick={onXlsx}>
          Excel (XLSX)
        </Button>
        <Typography variant="body2" color="text.secondary">
          {t.txCount(transactions.length)}
        </Typography>
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t.colDate}</TableCell>
              <TableCell>{t.colDesc}</TableCell>
              <TableCell align="right">{t.colAmount}</TableCell>
              <TableCell align="right">{t.colBalance}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((tx, i) => (
              <TableRow key={i} hover>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{tx.date}</TableCell>
                <TableCell>{tx.description}</TableCell>
                <TableCell
                  align="right"
                  sx={{
                    whiteSpace: "nowrap",
                    fontVariantNumeric: "tabular-nums",
                    color: tx.amount < 0 ? "error.main" : "success.main",
                  }}
                >
                  {fmt(tx.amount)}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                  {tx.balance != null ? fmt(tx.balance) : ""}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {warnings.length > 0 && (
        <Accordion variant="outlined" disableGutters sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2">{t.warningsTitle(warnings.length)}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack component="ul" spacing={0.5} sx={{ pl: 2, m: 0 }}>
              {warnings.map((w, i) => (
                <Typography key={i} component="li" variant="body2" color="text.secondary">
                  {w}
                </Typography>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
}

function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
